import { Trans } from '@lingui/react/macro';
import { Checkmark, Globe, X } from '@remarkable/ark-icons';
import { Tag, Typography } from '@remarkable/ark-web';
import type { paths } from 'apis/cloud-consent-api';

import {
  useConsentStatus,
  useCreateConsent,
  useRevokeConsent,
} from 'src/api/queries/consent';
import {
  Button,
  Divider,
  NotificationBox,
  Sheet,
  Spinner,
} from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { TestId } from 'src/testids';

type ConsentLevel =
  paths['/consent/v1']['post']['requestBody']['content']['application/json']['level'];

function ConsentStatusTag({ level }: { level?: ConsentLevel | null }) {
  switch (level) {
    case 'full':
      return (
        <Tag data-cy={TestId.TagConsentStatus}>
          <Checkmark />
          <Trans>Full access</Trans>
        </Tag>
      );
    case 'meta-data':
      return (
        <Tag data-cy={TestId.TagConsentStatus}>
          <Globe />
          <Trans>Metadata access</Trans>
        </Tag>
      );
    default:
      return (
        <Tag data-cy={TestId.TagConsentStatus}>
          <X /> <Trans>No access</Trans>
        </Tag>
      );
  }
}

function ConsentDescription({ level }: { level?: ConsentLevel | null }) {
  switch (level) {
    case null:
      return (
        <div data-cy={TestId.TextConsentDescription}>
          <Typography variant="body-md">
            <Trans>
              By granting access to metadata, you give us access to
              non-sensitive data on your paper tablet and reMarkable apps.
            </Trans>
          </Typography>
          <Typography variant="body-md">
            <Trans>
              Full access gives permission to view and edit all files on your
              paper tablet and apps. You can revoke access anytime.
            </Trans>
          </Typography>
        </div>
      );
    case 'meta-data':
      return (
        <Typography variant="body-md" data-cy={TestId.TextConsentDescription}>
          <Trans>
            To grant full access, first revoke metadata access, then choose full
            access.
          </Trans>
        </Typography>
      );
    default:
      return (
        <Typography variant="body-md" data-cy={TestId.TextConsentDescription}>
          <Trans>
            To grant metadata access, first revoke access, then permit metadata
            access.
          </Trans>
        </Typography>
      );
  }
}

function ConsentActions({
  consentStatus,
  updateConsent,
  revokeConsent,
}: {
  consentStatus: ReturnType<typeof useConsentStatus>;
  updateConsent: ReturnType<typeof useCreateConsent>;
  revokeConsent: ReturnType<typeof useRevokeConsent>;
}) {
  if (consentStatus.isPending) {
    return <Spinner inline />;
  }

  if (consentStatus.isError) {
    return null;
  }

  if (!consentStatus.data) {
    return (
      <div className="flex w-full flex-col justify-center gap-16 lm:flex-row">
        <Button
          className="w-full lm:w-fit"
          variant="secondary"
          data-cy={TestId.ButtonGrantMetadataAccess}
          onClick={() =>
            updateConsent.mutate({ level: 'meta-data', type: 'sync-content' })
          }
          loading={
            updateConsent.isPending &&
            updateConsent.variables?.level === 'meta-data'
          }
        >
          <Trans>Grant metadata access</Trans>
        </Button>
        <Button
          className="w-full lm:w-fit"
          data-cy={TestId.ButtonGrantFullAccess}
          onClick={() =>
            updateConsent.mutate({ level: 'full', type: 'sync-content' })
          }
          variant="secondary"
          loading={
            updateConsent.isPending && updateConsent.variables?.level === 'full'
          }
        >
          <Trans>Grant full access</Trans>
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant="secondary"
      data-cy={TestId.ButtonRevokeAccess}
      onClick={() => revokeConsent.mutate()}
      loading={revokeConsent.isPending}
      className="w-full lm:w-fit"
    >
      <Trans>Revoke access</Trans>
    </Button>
  );
}

export const UserConsentForSupportApp = () => {
  const consentStatus = useConsentStatus();
  const updateConsent = useCreateConsent();
  const revokeConsent = useRevokeConsent();

  return (
    <AppPage.Container>
      <AppPage.Header
        title={<Trans>Manage consent choices</Trans>}
        subtitle={<Trans>Review and manage your consent options.</Trans>}
      />
      <AppPage.Content className="max-w-lxl">
        {consentStatus.isError && (
          <NotificationBox
            className="my-32"
            data-cy={TestId.NotificationConsentStatusError}
            error={consentStatus.error}
            title={
              <Trans>Something went wrong getting your consent status</Trans>
            }
          />
        )}
        {(updateConsent.isError || revokeConsent.isError) && (
          <NotificationBox
            className="my-32"
            variant="error"
            title={
              <Trans>Something went wrong updating your consent status</Trans>
            }
          />
        )}
        <Sheet>
          <div className="flex flex-col gap-16 lm:flex-row lm:items-center lm:justify-between">
            <Typography variant="heading-subtle-xl">
              <Trans>Your consent status</Trans>
            </Typography>
            <div className="w-fit">
              {consentStatus.isPending ? (
                <Spinner inline />
              ) : (
                <ConsentStatusTag level={consentStatus.data?.level} />
              )}
            </div>
          </div>
          <Divider className="my-16" />
          <div className="flex flex-col items-center justify-center gap-32 text-center">
            <ConsentDescription level={consentStatus.data?.level ?? null} />
            <ConsentActions
              consentStatus={consentStatus}
              updateConsent={updateConsent}
              revokeConsent={revokeConsent}
            />
          </div>
        </Sheet>
      </AppPage.Content>
    </AppPage.Container>
  );
};
